import {loggerFactory} from "@cm/browser-log"
import {elementToScreenCoordinates, screenToElementCoordinates} from "@app/node-editor/helpers/coordinates"
import {Gesture, MouseButton, NavigationOptions, Point, TrackpadZoom} from "@app/node-editor/types/navigation"
import {Subject} from "rxjs"
import {constrainScale} from "@node-editor/helpers/scale"
import {trackpadZoom} from "@node-editor/helpers/trackpad"

export class NodeEditorTransform {
    public transform = new DOMMatrix()
    public transformChange = new Subject<{matrix: DOMMatrix; scale: number; translation: Point}>()
    private trackpadZoom: TrackpadZoom

    constructor(
        private parent: HTMLElement,
        public child: HTMLElement,
        private options: NavigationOptions = {
            panningButtons: [MouseButton.Right, MouseButton.Middle],
            scaleFactor: 1.15,
            maxScale: 4,
            minScale: 0.2,
        },
    ) {
        this.registerPan()
        this.registerZoom()
        this.preventBrowserGestures()

        let initialScale = 0
        let initialTranslation = {x: 0, y: 0}

        this.trackpadZoom = trackpadZoom(
            parent,
            {
                onGestureStart: (gesture) => {
                    initialScale = this.scale
                    initialTranslation = this.viewportOrigin
                    this.scaleAndTranslate(
                        {x: initialTranslation.x + gesture.translation.x, y: initialTranslation.y + gesture.translation.y},
                        initialScale * gesture.scale,
                    )
                },
                onGestureChange: (gesture) => {
                    this.scaleAndTranslate(
                        {x: initialTranslation.x + gesture.translation.x, y: initialTranslation.y + gesture.translation.y},
                        initialScale * gesture.scale,
                    )
                },
                onGestureEnd: (gesture: Gesture) => {
                    this.scaleAndTranslate(
                        {x: initialTranslation.x + gesture.translation.x, y: initialTranslation.y + gesture.translation.y},
                        initialScale * gesture.scale,
                    )
                },
            },
            (wheelEvent) => ({dx: wheelEvent.deltaX, dy: wheelEvent.deltaY}),
        )
    }

    public get scale() {
        return this.transform.a
    }

    public get logicalOrigin() {
        return {
            x: this.transform.e / this.scale,
            y: this.transform.f / this.scale,
        }
    }

    public get viewportOrigin() {
        return {
            x: this.transform.e,
            y: this.transform.f,
        }
    }

    // scale the viewport and then tranlate the origin (taking scale into account)
    private scaleAndTranslate = (viewportOrigin: Point, scale?: number) => {
        this.transform = new DOMMatrix().translate(viewportOrigin.x, viewportOrigin.y).scale(scale || this.scale)
        this.child.style.transform = this.transform.toString()
        this.transformChange.next({scale: scale || this.scale, translation: this.viewportOrigin, matrix: this.transform})
    }

    // add mouse panning (click and drag)
    private panOffset: {x: number; y: number} | null = null

    private mouseDown = (event: MouseEvent) => {
        if (this.options.panningButtons.includes(event.button)) {
            event.stopPropagation()
            this.panOffset = {x: event.clientX - this.viewportOrigin.x, y: event.clientY - this.viewportOrigin.y}
        }
    }

    private mouseMove = (event: MouseEvent) => {
        if (this.panOffset) {
            this.scaleAndTranslate({x: event.clientX - this.panOffset.x, y: event.clientY - this.panOffset.y})
        }
    }

    private mouseUp = (event: MouseEvent) => {
        if (this.panOffset) {
            event.stopPropagation()
            this.scaleAndTranslate({x: event.clientX - this.panOffset.x, y: event.clientY - this.panOffset.y})
            this.panOffset = null
            return
        }
    }

    private mouseLeave = (event: MouseEvent) => {
        if (this.panOffset) {
            event.stopPropagation()
            this.scaleAndTranslate({x: event.clientX - this.panOffset.x, y: event.clientY - this.panOffset.y})
            this.panOffset = null
            return
        }
    }

    private registerPan = () => {
        this.parent.addEventListener("mousedown", this.mouseDown)
        this.parent.addEventListener("mousemove", this.mouseMove)
        this.parent.addEventListener("mouseup", this.mouseUp)
        this.parent.addEventListener("mouseleave", this.mouseLeave)
    }

    private unregisterPan = () => {
        this.parent.removeEventListener("mousedown", this.mouseDown)
        this.parent.removeEventListener("mousemove", this.mouseMove)
        this.parent.removeEventListener("mouseup", this.mouseUp)
        this.parent.addEventListener("mouseleave", this.mouseLeave)
    }

    // we need to remember the last wheel event time to distinguish between mouse wheel and trackpad gestures
    // based on event frequency
    previousWheelEvent: {
        event: WheelEvent
        isDelayed: boolean
        timer: ReturnType<typeof setTimeout>
    } | null = null

    // add mousewheel zooming
    // note that dragging two fingers on a trackpad also triggers this event
    private mouseWheel = (event: WheelEvent & MouseEvent) => {
        // this does not prevent the deafult browser back/forward navigation,
        // we would need full screen for to do that...
        if (!(event instanceof WheelEvent)) {
            NodeEditorTransform.logger.warn("NodeEditorTransform: mouseWheel event is not a WheelEvent, ignoring", event)
            return
        }
        event.preventDefault()
        event.stopImmediatePropagation()

        // ignore accidental wheel events while panning
        if (this.panOffset) {
            return
        }

        const wheelDeltaY = "wheelDeltaY" in event && typeof event.wheelDeltaY === "number" ? event.wheelDeltaY : 0

        // we might need to refine this heuristic further, depending on the browsers and devices to be supported
        // see also https://github.com/w3c/uievents/issues/337
        const isTrackpad =
            (event.deltaMode === 0 && Math.abs(event.deltaY) < 20 && this.previousWheelEvent && Number.isInteger(event.deltaY)) ||
            !!event.deltaX ||
            Math.abs(wheelDeltaY) < 120

        // During trackpad pinch, the first event looks like a mouse wheel event,
        // so we need to wait a bit before we know how to handle it.
        // If no more events come in within 50 ms, we assume it's not a trackpad gesture.
        const initialDelay = 50

        const isFirstEvent = !this.previousWheelEvent
        if (this.previousWheelEvent) {
            clearTimeout(this.previousWheelEvent.timer)
        }

        if (isTrackpad) {
            if (this.previousWheelEvent?.isDelayed) {
                // console.log("Trackpad assumed for delayed event", event)
                this.trackpadZoom.wheelListener(this.previousWheelEvent.event)
            } else {
                // console.log("Trackpad assumed for follow-up event", event)
            }
            this.trackpadZoom.wheelListener(event)
        } else {
            if (this.previousWheelEvent?.isDelayed) {
                // console.log("Mouse wheel assumed for delayed event", event)
                this.handleMouseWheel(this.previousWheelEvent.event)
            } else {
                // console.log("Mouse wheel assumed for follow-up event", event)
            }
            this.handleMouseWheel(event)
        }
        this.previousWheelEvent = {
            event,
            isDelayed: isFirstEvent,
            timer: setTimeout(() => {
                if (isFirstEvent) {
                    // console.log("Mouse wheel assumed for singular event", event)
                    this.previousWheelEvent = null
                    this.handleMouseWheel(event)
                }
            }, initialDelay),
        }
    }

    private handleMouseWheel = (event: WheelEvent) => {
        const pointOnScreen = new DOMPoint(event.clientX, event.clientY)
        const pointWithinElement = screenToElementCoordinates(this.parent, pointOnScreen)
        const logicalPoint = this.editorToLogicalCoordinates(pointWithinElement)
        const newScale = constrainScale(this.scale * (event.deltaY < 0 ? this.options.scaleFactor : 1 / this.options.scaleFactor))
        this.transform = new DOMMatrix().translate(pointWithinElement.x, pointWithinElement.y).scale(newScale).translate(-logicalPoint.x, -logicalPoint.y)
        this.child.style.transform = this.transform.toString()
        this.transformChange.next({scale: this.scale, translation: this.viewportOrigin, matrix: this.transform})
    }

    private registerZoom = () => {
        this.parent.addEventListener("wheel", this.mouseWheel, {passive: false})
    }
    private unregisterZoom = () => {
        this.parent.removeEventListener("wheel", this.mouseWheel)
    }

    public unregister = () => {
        this.unregisterPan()
        this.unregisterZoom()
        this.allowBrowserGestures()
    }

    public viewportToLogicalCoordinates = (point: Point) => {
        return this.transform.inverse().transformPoint(screenToElementCoordinates(this.parent, point))
    }
    public logicalToViewportCoordinates = (point: Point) => {
        return elementToScreenCoordinates(this.parent, this.transform.transformPoint(point))
    }

    public editorToLogicalCoordinates = (point: Point) => {
        return this.transform.inverse().transformPoint(point)
    }
    public logicalToEditorCoordinates = (point: Point) => {
        return this.transform.transformPoint(point)
    }

    private allowBrowserGestures = () => {}
    private preventBrowserGestures() {
        const handler = (event: Event) => {
            event.preventDefault()
            event.stopImmediatePropagation()
            event.stopPropagation()
        }

        document.addEventListener("scroll", handler, {passive: false})

        this.allowBrowserGestures = () => {
            document.removeEventListener("scroll", handler)
        }
    }

    private static readonly logger = loggerFactory.getLogger("graph")
}
